import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Image as ImageIcon, Loader2, Search, Upload, Trash2, AlertTriangle,
  RefreshCw,
  Filter, ExternalLink, Sparkles, FolderOpen,
} from 'lucide-react';
import bridgeApi from './bridgeApi';
import ConfirmDialog from './ConfirmDialog';

// OL-served media URLs are stored relative (`/api/wp-media/file/...`).
// Absolutize against the public backend URL so <img>/window.open resolve.
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const mediaSrc = (u) => (u && u.startsWith('/api/') ? `${API_BASE}${u}` : u);

/**
 * MediaLibraryPanel — Full category-aware media library UI per
 * MEDIA_CLASSIFICATION_v1.md. Built for the WP Mgmt tab.
 *
 *  - 13-category dropdown filter (registry pulled at mount)
 *  - Grid view of media (thumbnails + slug + category badge)
 *  - "+ Upload" dialog with category dropdown → conditional fields →
 *    live slug preview → "renders on" preview
 *  - Delete with universal confirm dialog
 */
export default function MediaLibraryPanel({ orgId, onPickerSelect = null, defaultCategory = 'all' }) {
  const [registry, setRegistry] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(defaultCategory);
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  // Load category registry once
  useEffect(() => {
    bridgeApi.mediaRegistry().then(d => { if (d.ok) setRegistry(d.data); });
  }, []);

  const reload = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const opts = { limit: 1000, forceRefresh };
      if (filter !== 'all') opts.category = filter;
      const d = await bridgeApi.mediaList(orgId, opts);
      setItems(d.ok ? (d.data || []) : []);
    } finally {
      setLoading(false);
    }
  }, [orgId, filter]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = items.filter(m => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (m.title || '').toLowerCase().includes(s)
      || (m.slug || '').toLowerCase().includes(s)
      || (m.url || '').toLowerCase().includes(s);
  });

  const handleDelete = (m) => {
    setConfirmState({
      title: 'Delete this media file?',
      description: `${m.title || m.slug || `#${m.attachment_id}`} will be permanently removed from WordPress. Theme references using its slug will fall back to the global default. This cannot be undone.`,
      confirmLabel: 'Yes, delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await bridgeApi.mediaDelete(orgId, m.attachment_id, true);
          await reload();
        } catch (e) {
          alert(`Delete failed: ${e.response?.data?.detail || e.message}`);
        }
      },
    });
  };

  return (
    <div className="space-y-3" data-testid="media-library-panel">
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />

      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-amber-500" />
          <h2 className="text-base font-bold text-slate-100">Media Library</h2>
          <Badge variant="outline" className="bg-slate-700/40 border-slate-600 text-[10px]">
            {filtered.length} item{filtered.length === 1 ? '' : 's'}
          </Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search title, slug…"
              className="pl-7 bg-slate-800 border-slate-600 text-slate-100 h-8 text-xs w-48"
              data-testid="media-search"
            />
          </div>
          <select
            value={filter} onChange={e => setFilter(e.target.value)}
            className="h-8 text-xs rounded-md px-2 bg-slate-800 border border-slate-600 text-slate-100"
            data-testid="media-category-filter"
          >
            <option value="all">All categories</option>
            {registry.map(r => <option key={r.token} value={r.token}>{r.label}</option>)}
          </select>
          <Button
            size="sm" variant="outline"
            className="h-8 text-xs bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
            onClick={() => reload(true)}
            data-testid="media-refresh-btn"
            title="Re-sync the full media library from WordPress"
          ><RefreshCw className="h-3 w-3 mr-1" /> Refresh from WP</Button>
          <Button
            size="sm" className="bg-amber-600 hover:bg-amber-700 h-8 text-xs"
            onClick={() => setShowUpload(true)}
            data-testid="media-upload-btn"
          ><Upload className="h-3 w-3 mr-1" /> Upload</Button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400 border border-dashed border-slate-700 rounded-lg">
          <FolderOpen className="h-8 w-8 mx-auto mb-2 text-slate-600" />
          No media found. {filter !== 'all' && <button className="text-amber-400 underline" onClick={() => setFilter('all')}>Clear filter</button>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="media-grid">
          {filtered.map(m => {
            const catFromSlug = (m.slug || '').match(/^([a-z]+(?:-[a-z]+)?)-/)?.[1];
            const cls = m._ol_classification;
            return (
              <Card key={m.attachment_id} className="bg-slate-800/40 border-slate-700/60 overflow-hidden group hover:border-amber-600/40 transition-colors" data-testid={`media-card-${m.attachment_id}`}>
                <div className="aspect-square bg-slate-900 relative overflow-hidden">
                  {m.url ? (
                    <img src={mediaSrc(m.url_medium || m.url)} alt={m.title}
                      className="w-full h-full object-contain"
                      onError={e => {
                        // Broken medium rendition → fall back to the full-size
                        // URL once before hiding. Several WP thumbnails 404
                        // when the intermediate size was never generated.
                        const img = e.currentTarget;
                        const full = mediaSrc(m.url);
                        if (m.url && img.src !== full && !img.dataset.fellBack) {
                          img.dataset.fellBack = '1';
                          img.src = full;
                        } else {
                          img.style.display = 'none';
                        }
                      }} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-600">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  )}
                  {/* Hover overlay with delete */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {onPickerSelect && (
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-7 text-[11px]" onClick={() => onPickerSelect(m)}>
                        Select
                      </Button>
                    )}
                    {m.url && (
                      <Button size="icon" variant="secondary" className="h-7 w-7 bg-slate-700/80" onClick={() => window.open(mediaSrc(m.url), '_blank')}>
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    )}
                    {!onPickerSelect && (
                      <Button size="icon" variant="secondary" className="h-7 w-7 bg-red-900/80 hover:bg-red-800" onClick={() => handleDelete(m)} data-testid={`media-delete-${m.attachment_id}`}>
                        <Trash2 className="h-3 w-3 text-red-200" />
                      </Button>
                    )}
                  </div>
                </div>
                <CardContent className="p-2">
                  <p className="text-[11px] text-slate-100 truncate font-medium" title={m.title}>{m.title || '(untitled)'}</p>
                  <p className="text-[10px] text-emerald-400 font-mono truncate" title={m.slug}>{m.slug || '(no slug)'}</p>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {cls?.category ? (
                      <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[9px]" title={cls.renders_on}>{cls.category}</Badge>
                    ) : catFromSlug && registry.some(r => r.token === catFromSlug) ? (
                      <Badge className="bg-slate-700/60 text-slate-300 border-slate-600 text-[9px]">{catFromSlug}</Badge>
                    ) : (
                      <Badge className="bg-slate-700/30 text-slate-500 border-slate-600/50 text-[9px]">unclassified</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <UploadDialog
        open={showUpload}
        registry={registry}
        orgId={orgId}
        items={items}
        onClose={() => setShowUpload(false)}
        onUploaded={async () => { await reload(true); }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// UPLOAD DIALOG
// ═══════════════════════════════════════════════════════════
function UploadDialog({ open, registry, orgId, items, onClose, onUploaded }) {
  const [category, setCategory] = useState('');
  const [fields, setFields] = useState({});
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [computed, setComputed] = useState(null); // {slug, renders_on}
  const [previewError, setPreviewError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const cat = registry.find(r => r.token === category);

  // Existing canonical team tokens + full team-photo slugs (from the live mirror),
  // so the user can pick a real, in-use slug instead of typing a non-canonical one.
  const teamPhotoSlugs = (items || [])
    .map(m => m.slug || '')
    .filter(s => /-team-photo(-|$)/.test(s));
  const teamTokens = [...new Set(
    teamPhotoSlugs
      .map(s => (s.match(/^([a-z]{2}-[a-z]{2,4})-team-photo/) || [])[1])
      .filter(Boolean)
  )].sort();
  const slugsForTeam = (tok) => [...new Set(
    teamPhotoSlugs.filter(s => s.startsWith(`${tok}-team-photo`))
  )].sort();
  const seasonOptions = [...new Set([
    ...teamPhotoSlugs.map(s => (s.match(/-team-photo-(\d{4}[a-z-]*)/) || [])[1]).filter(Boolean),
    '2024', '2025', '2026', '2027',
  ])].sort();

  const reset = () => {
    setCategory(''); setFields({}); setFile(null); setTitle('');
    setComputed(null); setPreviewError(null); setUploadError(null); setUploading(false);
  };

  // Live slug preview — debounce by triggering only when fields change and category is set
  useEffect(() => {
    if (!category) { setComputed(null); setPreviewError(null); return; }
    const required = (cat?.fields || []).filter(f => f.required);
    const allFilled = required.every(f => (fields[f.key] || '').toString().trim().length > 0);
    if (!allFilled) { setComputed(null); setPreviewError(null); return; }

    let cancelled = false;
    bridgeApi.mediaPreviewSlug(category, fields).then(d => {
      if (cancelled) return;
      if (d.ok) { setComputed(d.data); setPreviewError(null); }
      else { setComputed(null); setPreviewError(d.error?.message || 'Could not compute slug'); }
    }).catch(e => { if (!cancelled) setPreviewError(e.message); });

    return () => { cancelled = true; };
  }, [category, fields, cat]);

  const handleUpload = async () => {
    if (!file || !computed) return;
    setUploading(true); setUploadError(null);
    try {
      const res = await bridgeApi.mediaUploadClassified(orgId, file, category, fields, title || null);
      if (res.ok) {
        onUploaded?.();
        reset();
        onClose?.();
      } else {
        setUploadError(res.error?.message || 'Upload failed');
      }
    } catch (e) {
      setUploadError(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !uploading) { reset(); onClose?.(); } }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="media-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-amber-500" /> Upload Media
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Pick a category — OmniLeagues computes the WP slug + tells you exactly where the image will render.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Category dropdown */}
          <div className="space-y-1">
            <Label className="text-slate-200 text-xs">Category</Label>
            <select
              value={category}
              onChange={e => { setCategory(e.target.value); setFields({}); }}
              className="w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
              data-testid="upload-category-select"
            >
              <option value="">Select a category…</option>
              {registry.map(r => (
                <option key={r.token} value={r.token}>{r.label} — {r.description}</option>
              ))}
            </select>
          </div>

          {/* Conditional fields */}
          {cat && (
            <div className="space-y-2 p-3 rounded-md bg-slate-800/50 border border-slate-700/60">
              {cat.fields.map(f => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-slate-300 text-[11px]">
                    {f.label} {f.required && <span className="text-red-400">*</span>}
                  </Label>
                  {f.type === 'select' ? (
                    <select
                      value={fields[f.key] || ''}
                      onChange={e => setFields(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full h-8 px-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-xs"
                      data-testid={`upload-field-${f.key}`}
                    >
                      <option value="">Select…</option>
                      {f.options.map(opt => <option key={opt.v} value={opt.v}>{opt.l}</option>)}
                    </select>
                  ) : (category === 'team-photo' && f.key === 'team' && teamTokens.length > 0) ? (
                    <>
                      <select
                        value={fields[f.key] || ''}
                        onChange={e => setFields(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full h-8 px-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-xs"
                        data-testid={`upload-field-${f.key}`}
                      >
                        <option value="">Select a canonical team slug…</option>
                        {teamTokens.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      {fields.team && slugsForTeam(fields.team).length > 0 && (
                        <p className="text-[10px] text-slate-500">
                          Existing: {slugsForTeam(fields.team).join(', ')}
                        </p>
                      )}
                    </>
                  ) : (category === 'team-photo' && f.key === 'season') ? (
                    <select
                      value={fields[f.key] || ''}
                      onChange={e => setFields(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full h-8 px-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-xs"
                      data-testid={`upload-field-${f.key}`}
                    >
                      <option value="">Select a season…</option>
                      {seasonOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <Input
                      value={fields[f.key] || ''}
                      onChange={e => setFields(p => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.placeholder || ''}
                      className="bg-slate-900 border-slate-700 text-slate-100 h-8 text-xs"
                      data-testid={`upload-field-${f.key}`}
                    />
                  )}
                  {f.help && <p className="text-[10px] text-slate-500">{f.help}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Preview banner */}
          {computed && (
            <div className="p-2 rounded-md bg-emerald-900/20 border border-emerald-700/40 space-y-1" data-testid="upload-preview">
              <div className="flex items-center gap-1.5 text-emerald-300 text-[10px] uppercase tracking-wider">
                <Sparkles className="h-3 w-3" /> Preview
              </div>
              <p className="text-xs"><span className="text-slate-400">Slug:</span> <code className="text-emerald-400 font-mono text-[11px]">{computed.slug}</code></p>
              <p className="text-xs"><span className="text-slate-400">Renders on:</span> <span className="text-emerald-200">{computed.renders_on}</span></p>
            </div>
          )}
          {previewError && (
            <div className="p-2 rounded-md bg-red-900/20 border border-red-700/40 text-red-300 text-xs flex items-start gap-1.5">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{previewError}
            </div>
          )}

          {/* File */}
          <div className="space-y-1">
            <Label className="text-slate-200 text-xs">File (max 10 MB)</Label>
            <Input
              type="file"
              accept="image/*,application/pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="bg-slate-800 border-slate-700 text-slate-100 h-9 text-xs file:mr-2 file:bg-slate-700 file:text-slate-200 file:border-0 file:rounded file:px-2 file:py-1"
              data-testid="upload-file-input"
            />
            {file && (
              <p className="text-[10px] text-slate-400">
                {file.name} — {(file.size / 1024).toFixed(0)} KB — {file.type}
              </p>
            )}
          </div>

          {/* Title (optional) */}
          <div className="space-y-1">
            <Label className="text-slate-200 text-xs">Title (optional, defaults to slug)</Label>
            <Input
              value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Human-readable title"
              className="bg-slate-800 border-slate-700 text-slate-100 h-8 text-xs"
              data-testid="upload-title"
            />
          </div>

          {uploadError && (
            <div className="p-2 rounded-md bg-red-900/20 border border-red-700/40 text-red-300 text-xs flex items-start gap-1.5">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{uploadError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline" disabled={uploading}
            onClick={() => { reset(); onClose?.(); }}
            className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
          >Cancel</Button>
          <Button
            disabled={uploading || !file || !computed}
            onClick={handleUpload}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="upload-submit"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
